import React, { useEffect, useState, useContext } from "react";
import H5 from "components/typography/H5";
import { Box, Divider, Flex, Grid, Label, Link, Spinner, Badge, Button } from "theme-ui";
import { themevals } from "theme/themevals";
import FileUpload from "./FileUpload";
import { getProjectFiles, deleteProjectFile, addProjectFile, shareProjectAsset } from "api/Projects";
import ProviderShareSelect from "components/ui/ProviderShareSelect";
import { getPresignedUrl } from "api/Media";
import moment from "moment-timezone";
import { createColumnHelper } from "@tanstack/react-table";
import TableBasic from "components/ui/Table";
import { TrashIcon, QuestionMarkIcon, EllipsisIcon } from "components/svg";
import BasicModal from "components/ui/BasicModal";
import BasicButton from "components/forms/BasicButton";
import P from "components/typography/P";
import { useToast } from "components/toast";
import UserContext from "context/User";
import { trackActivity } from "api/ActivityTracker";

const Files = ({ isCompany, project, isProvider }: { isCompany: boolean; project: any; isProvider: boolean }) => {
  const [files, setFiles] = useState<any[]>([]);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState<any>(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const dropInputRef = React.useRef<HTMLInputElement>(null);
  const columnHelper = createColumnHelper();
  const toast = useToast();
  const { user } = useContext<any>(UserContext);

  useEffect(() => {
    _init();
  }, []);

  const _init = async () => {
    const res = await getProjectFiles({ data: project });
    if (res?.length) {
      console.log("res", res);
      setFiles(res);
    } else {
      setFiles([]);
    }
  };

  const handleDirectUpload = async (file: File) => {
    if (!file || !project?.id) return;
    setUploading(true);
    try {
      const signed = await getPresignedUrl({ data: { directory: 'projects/' + project.id + '/files', name: file.name, type: file.type, size: file.size } });
      if (signed?.success && signed?.url) {
        const putRes = await fetch(signed.url, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
        if (putRes.ok) {
          const reg = await addProjectFile({ id: project.id, data: { url: signed.location, name: file.name } });
          if (reg?.id) {
            void trackActivity({ module: 'projects', action: 'upload_project_file', actionType: 'create', userId: user?.id || null, companyId: user?.company?.id || null, entityType: 'project', entityId: project.id, metadata: { fileId: reg.id, fileName: file.name }, userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null });
            toast.add('File uploaded.', 'var(--theme-ui-colors-success)');
            _init();
          } else { toast.add('Error registering file.', 'var(--theme-ui-colors-danger)'); }
        } else { toast.add('Error uploading file.', 'var(--theme-ui-colors-danger)'); }
      } else { toast.add('Error getting upload URL.', 'var(--theme-ui-colors-danger)'); }
    } catch (e) { toast.add('Upload failed.', 'var(--theme-ui-colors-danger)'); }
    setUploading(false);
  };

  const handleDrop = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); const file = e.dataTransfer.files?.[0]; if (file) handleDirectUpload(file); };
  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); };
  const handleDragLeave = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); };
  const handleDropInputChange = (e: React.ChangeEvent<HTMLInputElement>) => { const file = e.target.files?.[0]; if (file) handleDirectUpload(file); e.target.value = ''; };

  const _confirmFileDelete = async (file: any) => {
    setConfirmModalOpen(true);
    setSelectedFile(file);
  };

  const _cancelConfirmModal = () => {
    setConfirmModalOpen(false);
    setSelectedFile(null);
  };

  const _deleteFile = async (file: any) => {
    setSubmitting(true);
    const res = await deleteProjectFile({ data: { id: project.id, fileId: file.id } });
    if (res?.success) {
      void trackActivity({
        module: "projects",
        action: "delete_project_file",
        actionType: "delete",
        userId: user?.id || null,
        companyId: user?.company?.id || null,
        entityType: "project",
        entityId: project.id,
        metadata: {
          fileId: file.id,
          fileName: file.name ?? null,
        },
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
      setSubmitting(false);
      toast.add(`File deleted.`, "var(--theme-ui-colors-success)");
      setConfirmModalOpen(false);
      setSelectedFile(null);
      _init();
    } else {
      setSubmitting(false);
      toast.add(`Error deleting file.`, "var(--theme-ui-colors-danger)");
      setConfirmModalOpen(false);
      setSelectedFile(null);
    }
  };

  const columns = [
    columnHelper.accessor((row: any) => row?.name, {
      id: "id",
      cell: (info: any) => {
        const row = info.row.original;
        return (
          <Box sx={{ color: themevals.colors.black, cursor: "pointer" }} onClick={() => window.open(row?.url, "_blank")}>
            {info.getValue()}
            {/* Label set when the file was added (2026-10-12): kind + PO number / description. */}
            {(row?.kind || row?.description) && (
              <Box sx={{ fontSize: "12px", color: themevals.colors.dkgrey, marginTop: "2px" }}>
                {[({ po: "Purchase order", site_map: "Site map", photo: "Site photo", other: "Other" } as any)[row?.kind] , row?.kind === "po" && row?.description ? "PO# " + row.description : row?.description]
                  .filter(Boolean)
                  .join(" · ")}
              </Box>
            )}
          </Box>
        );
      },
      header: () => <span>File</span>,
    }),
    columnHelper.accessor((row: any) => row?.createdAt, {
      id: "dateAdded",
      cell: (info: any) => (info.getValue() ? moment(info.getValue()).format("MMM D, YYYY") : ""),
      header: () => <span>Date Added</span>,
    }),
    columnHelper.accessor((row: any) => row?.uploadedBy, {
      id: "uploadedBy",
      cell: (info: any) => `${info.getValue()?.firstName} ${info.getValue()?.lastName}`,
      header: () => <span>UploadedBy</span>,
    }),
    ...(isCompany
      ? [
          columnHelper.accessor((row: any) => row?.sharedWithProviderId, {
            id: "providerShare",
            cell: (info: any) => {
              const row = info.row.original;
              return (
                <ProviderShareSelect
                  projectId={project.id}
                  value={row?.sharedWithProviderId}
                  onChange={async (providerId) => {
                    const res = await shareProjectAsset({ projectId: project.id, kind: "files", assetId: row.id, providerId });
                    if (res?.success) _init();
                    else toast.add(res?.message || "Could not change sharing.", "var(--theme-ui-colors-danger)");
                  }}
                />
              );
            },
            header: () => <span>Provider</span>,
            enableSorting: false,
          }),
        ]
      : []),
    columnHelper.accessor("actions", {
      id: "actions",
      cell: (info: any) => {
        const row = info.row.original;
        if (row?.uploadedBy?.id !== user?.id) return null;
        return (
          <Box onClick={() => _confirmFileDelete(row)} sx={{ cursor: "pointer" }}>
            <TrashIcon fill={themevals.colors.dkgrey} width="14px" height="14px" />
          </Box>
        );
      },
      header: () => null,
      enableSorting: false,
      meta: {
        style: { width: "10px" },
      },
    }),
  ];

  return (
    <>
      <H5 fontFamily="Rubik" fontSize="16px" marginLeft="0px" marginTop="0px">
        Files
      </H5>
      {files?.length > 0 && (
        <>
          <Flex sx={{ justifyContent: "flex-start", mb: 2 }}>{!isProvider && <FileUpload title={"Add File"} project={project} afterUpload={_init} />}</Flex>
          <TableBasic data={files} columns={columns} enablePaging={false} enableSearch={false} customStyles={{ border: `1px solid ${themevals.colors.inputOutline}` }} />
        </>
      )}
      {files?.length === 0 && (
        <Flex
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => !uploading && dropInputRef.current?.click()}
          sx={{
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "300px",
            border: "2px dashed",
            borderColor: dragActive ? "primary" : "inputOutline",
            backgroundColor: dragActive ? "rgba(0,0,0,0.02)" : "transparent",
            borderRadius: "12px",
            padding: "40px",
            mt: 2,
            cursor: uploading ? "wait" : "pointer",
            transition: "border-color 0.15s, background-color 0.15s",
          }}>
          <Box sx={{ width: 48, height: 48, mb: 3, opacity: 0.3 }}>
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Box>
          <P fontFamily="Poppins" color={themevals.colors.dkgrey} fontSize="14px" fontWeight={500} marginTop="0px" marginBottom="4px">
            No files yet
          </P>
          <P fontFamily="Poppins" color={themevals.colors.dkgrey} fontSize="12px" fontWeight={400} marginTop="0px" marginBottom="16px" style={{ opacity: 0.7 }}>
            {uploading ? "Uploading..." : "Drag and drop or click to browse"}
          </P>
          {uploading && <Spinner size={20} strokeWidth={3} />}
          <input ref={dropInputRef} type="file" hidden onChange={handleDropInputChange} />
        </Flex>
      )}
      {confirmModalOpen && (
        <BasicModal
          title={`Delete File?`}
          cancel_click={() => _cancelConfirmModal()}
          cancel_text="No, cancel"
          $submitting={submitting}
          size={"sm"}
          icon={<QuestionMarkIcon fill={"#818181"} height="30px" />}
          actions={
            <BasicButton
              type="button"
              scheme="danger"
              onClick={() => _deleteFile(selectedFile)}
              $submitting={submitting}
              $iconHeight="14px"
              $iconWidth="14px"
              $loadingColor="#fff"
              keepText={true}
              styles={{ margin: "0 0.625rem .25rem 0" }}
            >
              {!submitting && <TrashIcon fill={"#fff"} />}
              {"Delete File"}
            </BasicButton>
          }
        >
          <P lineHeight={"22px"}>Are you sure you want to delete the file from the project?</P>
        </BasicModal>
      )}
    </>
  );
};

export default Files;
